import mainStyle from "./mainStyle.module.scss";

import { ReactComponent as AddBtn } from "assets/images/icons/btn_add.svg";
import { ReactComponent as AddBtnHover } from "assets/images/icons/btn_add_hover.svg";
import { ReactComponent as ExportIcon } from "assets/images/icons/icn_btn_export.svg";
import icon_refresh from "assets/images/icons/icn_refresh.svg";
import btn_rediscover from "assets/images/icons/icn_btn_rediscover.svg";
import btn_inventory from "assets/images/icons/icn_table_cell_inventory.svg";
import btn_provision_app from "assets/images/icons/icn_table_cell_scan_app.svg";
import btn_pre_register from "assets/images/icons/icn_table_cell_address.svg";

import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import {
  Divider,
  Grid,
  Box,
  ClickAwayListener,
  CircularProgress,
} from "@mui/material";
import { createColumnHelper } from "@tanstack/react-table";
import { v4 as uuidv4 } from "uuid";
import { cloneDeep } from "lodash";
import useZustandStore from "zustandStore";

import { useWebSocket } from "contexts/WebSocketContext";
import useDataSourceManager from "hooks/useDataSourceManager";
import useTableInstance from "hooks/useTableInstance";
import { useAuth } from "contexts/AuthContext";
import useAuthScope from "hooks/useAuthScope";

import Discover from "./AddDevice/discover";
import { SolidButton } from "components/buttons/SolidButton";
import TabsField from "components/Tab";
import SearchInput from "components/SearchInput";
import ColumnVisibilityToggle from "components/Table/ColumnVisibilityToggle";
import TableField from "components/Table";
import DrawerField from "components/Drawer";

import allDeviceColumns from "./AllDevices/allDeviceColumns";
import accessPointColumns from "./AccessPoint/accessPointColumns";
import switchColumns from "./Switch/switchColumns";
import routerColumns from "./Router/routerColumns";
import inventoryColumns from "./Inventory/inventoryColumns";
import proxyColumns from "./Proxy/proxyColumns";
import ActionButton from "./ActionButton";
import ProvisionAppModal from "./AddDevice/modals/ModalProvisionApp";
import PreRegisterModal from "./AddDevice/modals/ModalPreRegister";

import {
  handleTableDataRequest,
  handleSiteInventoryRequest,
  handleSiteProxyDevicesRequest,
} from "./helper";
import {
  ALL_VISIBILITY_GROUP_DATA,
  AP_VISIBILITY_GROUP_DATA,
  SW_VISIBILITY_GROUP_DATA,
  RTR_VISIBILITY_GROUP_DATA,
  INV_VISIBILITY_GROUP_DATA,
  PROXY_VISIBILITY_GROUP_DATA,
} from "./const";

import { exportTableDataToCSV } from "utils/export";

const checkEnvironment = process.env.REACT_APP_RUNNING_CONTEXT;
const checkMockup = process.env.REACT_APP_IS_MOCKUP;

const Devices = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { auth, standalone } = useAuthScope();
  const { token } = useAuth();
  const portalConfig = useZustandStore((s) => s.portalConfig);

  const [licenseDataLoaded, setLicenseDataLoaded] = useState(false);
  const [settingsData, setSettingsData] = useState(null);
  const settingsUuid = useMemo(() => uuidv4(), []);

  const [clickProvisionApp, setClickProvisionApp] = useState(false);
  const [clickDiscover, setClickDiscover] = useState(false);
  const [clickSiteInventory, setClickSiteInventory] = useState(false);
  const [clickSiteUnityProxy, setClickSiteUnityProxy] = useState(false);
  const [showProvisionApp, setShowProvisionApp] = useState(false);
  const [showPreRegisterModal, setShowPreRegisterModal] = useState(false);
  const [reloadSiteInventory, setReloadSiteInventory] = useState(false);

  const onTab = useMemo(() => {
    return !clickProvisionApp &&
      !clickDiscover &&
      !clickSiteInventory &&
      !clickSiteUnityProxy
      ? true
      : false;
  }, [
    auth.scopeLevel,
    clickProvisionApp,
    clickDiscover,
    clickSiteInventory,
    clickSiteUnityProxy,
  ]);

  const [widgetAllUuid, _setWidgetAllUuid] = useState(uuidv4());
  const [widgetApUuid, _setWidgetApUuid] = useState(uuidv4());
  const [widgetSwUuid, _setWidgetSwUuid] = useState(uuidv4());
  const [widgetRtrUuid, _setWidgetRtrUuid] = useState(uuidv4());
  const [widgetInvUuid, _setWidgetInvUuid] = useState(uuidv4());
  const [widgetProxyUuid, _setWidgetProxyUuid] = useState(uuidv4());
  const [widgetSiteInvUuid, _setWidgetSiteInvUuid] = useState(uuidv4());
  const [isEmptyTable, setIsEmptyTable] = useState(false);
  const [openAddDeviceAction, setOpenAddDeviceAction] = useState(false);
  const [discoverBatchActionTarget, setDiscoverBatchActionTarget] =
    useState(null);
  const [discoverBatchActionsStatus, setDiscoverBatchActionsStatus] = useState({
    ipSetting: false,
    auth: false,
    adopt: false,
  });

  const [loading, setLoading] = useState(false);
  const [tabCountAll, setTabCountAll] = useState(0);
  const [tabCountAp, setTabCountAp] = useState(0);
  const [tabCountSw, setTabCountSw] = useState(0);
  const [tabCountRtr, setTabCountRtr] = useState(0);
  const [tabCountInv, setTabCountInv] = useState(0);
  const [tabCountProxy, setTabCountProxy] = useState(0);
  const [tabVal, setTabVal] = useState("all_device");
  const [preTab, setPreTab] = useState("all_device"); // This is for action button
  const [preTabForAutoDiscover, setPreTabForAutoDiscover] = useState(null); // This is for auto discover

  const currentVisibilityGroupData = useMemo(() => {
    if (onTab) {
      return (
        (tabVal === "all_device" && ALL_VISIBILITY_GROUP_DATA) ||
        (tabVal === "access_point" && AP_VISIBILITY_GROUP_DATA) ||
        (tabVal === "switch" && SW_VISIBILITY_GROUP_DATA) ||
        (tabVal === "router" && RTR_VISIBILITY_GROUP_DATA) ||
        (tabVal === "inventory" && INV_VISIBILITY_GROUP_DATA) ||
        (tabVal === "proxy" && PROXY_VISIBILITY_GROUP_DATA)
      );
    }
  }, [tabVal]);

  // Handle Proxy Devices for discover page
  const ws = useWebSocket();
  const [proxyDevicesUuid, _setProxyDevicesUuid] = useState(uuidv4());
  const [haveProxyDevices, setHaveProxyDevices] = useState(false);
  const [discoverProxyDevices, setDiscoverProxyDevices] = useState([]);

  const { navigateDeviceName, navigateDeviceId } = location.state ?? {};

  const handleTab = (event, newValue) => {
    handleDrawerClose();
    setIsEmptyTable(false);
    setTabVal(newValue);
    setOpenAction(false);
    setDiscoverBatchActionsStatus({
      ipSetting: false,
      auth: false,
      adopt: false,
    });

    setClickProvisionApp(false);
    setClickDiscover(false);
    if (auth.scopeLevel === "site") {
      setClickSiteInventory(false);
      setClickSiteUnityProxy(false);
    }
  };

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [currentDrawerData, setCurrentDrawerData] = useState({});

  function handleDrawerClose() {
    setCurrentDrawerData({});
    setDrawerOpen(false);
  }

  const [searchVal, setSearchVal] = useState("");

  const [openAction, setOpenAction] = useState(false);

  const columnHelper = createColumnHelper();

  const [tableDataAll, setTableDataAll] = useState([]);
  const [tableDataNetworkAll, setTableDataNetworkAll] = useState([]);
  const [tableDataCapabilitiesAll, setTableDataCapabilitiesAll] = useState([]);
  const [tableDataPortsAll, setTableDataPortsAll] = useState([]);
  const [tableDataInternetAll, setTableDataInternetAll] = useState([]);
  const [tableDataDhcpAll, setTableDataDhcpAll] = useState([]);
  const [tableDataManagedDevicesAll, setTableDataManagedDevicesAll] = useState(
    [],
  );
  const [tableDataWirelessRadioAll, setTableDataWirelessRadioAll] = useState(
    [],
  );
  const [tableDataWirelessNetworkAll, setTableDataWirelessNetworkAll] =
    useState([]);
  const [tableDataApRadioAll, setTableDataApRadioAll] = useState([]);
  const [mergedAllTableData, setMergedAllTableData] = useState([]); //info + radio data + upLink

  const allDevicesTableInstance = useTableInstance({
    tableName: "device",
    data: mergedAllTableData,
    columns: allDeviceColumns(columnHelper, auth.permissions),
    getRowId: (row) => row.id,
    enableSorting: true,
    enablePagination: true,
    enableGlobalFilter: true,
    hiddenColumns: [
      "hardwareVersion",
      "serialNumber",
      "licenseStatus",
      "firmwareVersion",
      "2.4GClients",
      "5GClients",
      "6GClients",
      "publicIpAddr",
      "cpuStats",
      "memoryStats",
      "description",
      "address",
      "clients",
      "deviceTag",
      "joinedOn",
    ],
    enableColumnPinning: true,
    defaultPinning: { left: ["select", "linkStatus", "deviceName"], right: [] },
    enableColumnOrder: true,
    enableRowSelection: true,
  });

  const [tableDataAp, setTableDataAp] = useState([]);
  const [tableDataNetworkAp, setTableDataNetworkAp] = useState([]);
  const [tableDataCapabilitiesAp, setTableDataCapabilitiesAp] = useState([]);
  const [tableDataPortsAp, setTableDataPortsAp] = useState([]);
  const [tableDataWirelessRadioAp, setTableDataWirelessRadioAp] = useState([]);
  const [tableDataWirelessNetworkAp, setTableDataWirelessNetworkAp] = useState(
    [],
  );
  const [tableDataApRadioAp, setTableDataApRadioAp] = useState([]);
  const [mergedApTableData, setMergedApTableData] = useState([]); //info + radio data + upLink
  const apTableInstance = useTableInstance({
    tableName: "device",
    data: mergedApTableData,
    columns: accessPointColumns(columnHelper, auth.permissions),
    getRowId: (row) => row.id,
    enableSorting: true,
    enablePagination: true,
    enableGlobalFilter: true,
    hiddenColumns: [
      "hardwareVersion",
      "serialNumber",
      "licenseStatus",
      "firmwareVersion",
      "2.4GClients",
      "5GClients",
      "6GClients",
      "publicIpAddr",
      "cpuStats",
      "memoryStats",
      "description",
      "address",
      "clients",
      "deviceTag",
      "joinedOn",
    ],
    enableColumnPinning: true,
    defaultPinning: { left: ["select", "linkStatus", "deviceName"], right: [] },
    enableColumnOrder: true,
    enableRowSelection: true,
  });

  const [tableDataSw, setTableDataSw] = useState([]);
  const [tableDataNetworkSw, setTableDataNetworkSw] = useState([]);
  const [tableDataCapabilitiesSw, setTableDataCapabilitiesSw] = useState([]);
  const [tableDataPortsSw, setTableDataPortsSw] = useState([]);
  const [mergedSwTableData, setMergedSwTableData] = useState([]); //info + upLink

  const swTableInstance = useTableInstance({
    tableName: "device",
    data: mergedSwTableData,
    columns: switchColumns(columnHelper, auth.permissions),
    getRowId: (row) => row.id,
    enableSorting: true,
    enablePagination: true,
    enableGlobalFilter: true,
    hiddenColumns: [
      "hardwareVersion",
      "serialNumber",
      "licenseStatus",
      "firmwareVersion",
      "publicIpAddr",
      "cpuStats",
      "memoryStats",
      "description",
      "address",
      "clients",
      "deviceTag",
      "joinedOn",
    ],
    enableColumnPinning: true,
    defaultPinning: { left: ["select", "linkStatus", "deviceName"], right: [] },
    enableColumnOrder: true,
    enableRowSelection: true,
  });

  const [tableDataRtr, setTableDataRtr] = useState([]);
  const [tableDataNetworkRtr, setTableDataNetworkRtr] = useState([]);
  const [tableDataCapabilitiesRtr, setTableDataCapabilitiesRtr] = useState([]);
  const [tableDataInternetRtr, setTableDataInternetRtr] = useState([]);
  const [tableDataDhcpRtr, setTableDataDhcpRtr] = useState([]);
  const [tableDataPortsRtr, setTableDataPortsRtr] = useState([]);
  const [mergedRtrTableData, setMergedRtrTableData] = useState([]); //info + upLink

  const rtrTableInstance = useTableInstance({
    tableName: "device",
    data: mergedRtrTableData,
    columns: routerColumns(columnHelper, auth.permissions),
    getRowId: (row) => row.id,
    enableSorting: true,
    enablePagination: true,
    enableGlobalFilter: true,
    hiddenColumns: [
      "hardwareVersion",
      "serialNumber",
      "licenseStatus",
      "firmwareVersion",
      "publicIpAddr",
      "cpuStats",
      "memoryStats",
      "description",
      "address",
      "clients",
      "deviceTag",
      "joinedOn",
    ],
    enableColumnPinning: true,
    defaultPinning: { left: ["select", "linkStatus", "deviceName"], right: [] },
    enableColumnOrder: true,
    enableRowSelection: true,
  });

  const [tableDataInv, setTableDataInv] = useState([]);
  const [tableDataNetworkInv, setTableDataNetworkInv] = useState([]);
  const [tableDataCapabilitiesInv, setTableDataCapabilitiesInv] = useState([]);
  const [tableDataPortsInv, setTableDataPortsInv] = useState([]);
  const [tableDataWirelessRadioInv, setTableDataWirelessRadioInv] = useState(
    [],
  );
  const [tableDataWirelessNetworkInv, setTableDataWirelessNetworkInv] =
    useState([]);
  const [tableDataApRadioInv, setTableDataApRadioInv] = useState([]);
  const [mergedInvTableData, setMergedInvTableData] = useState([]); //info + upLink

  const invTableInstance = useTableInstance({
    tableName: "inventory",
    data: mergedInvTableData,
    columns: inventoryColumns(columnHelper, auth.permissions),
    getRowId: (row) => row.id,
    enableSorting: true,
    enablePagination: true,
    enableGlobalFilter: true,
    hiddenColumns: [
      "hardwareVersion",
      "serialNumber",
      "licenseStatus",
      "firmwareVersion",
      "publicIpAddr",
      "description",
      "address",
      "clients",
      "deviceTag",
      "joinedOn",
    ],
    enableColumnPinning: true,
    defaultPinning: { left: ["select", "linkStatus", "deviceName"], right: [] },
    enableColumnOrder: true,
    enableRowSelection: true,
  });

  const [tableDataProxy, setTableDataProxy] = useState([]);
  const [tableDataNetworkProxy, setTableDataNetworkProxy] = useState([]);
  const [tableDataCapabilitiesProxy, setTableDataCapabilitiesProxy] = useState(
    [],
  );
  const [tableDataManagedDevicesProxy, setTableDataManagedDevicesProxy] =
    useState([]);
  const [tableDataPortsProxy, setTableDataPortsProxy] = useState([]);
  const [mergedProxyTableData, setMergedProxyTableData] = useState([]); //info + upLink

  const proxyTableInstance = useTableInstance({
    tableName: "proxy",
    data: mergedProxyTableData,
    columns: proxyColumns(columnHelper, auth.permissions),
    getRowId: (row) => row.id,
    enableSorting: true,
    enablePagination: true,
    enableGlobalFilter: true,
    hiddenColumns: [
      "hardwareVersion",
      "serialNumber",
      "licenseStatus",
      "firmwareVersion",
      "2.4GClients",
      "5GClients",
      "6GClients",
      "publicIpAddr",
      "cpuStats",
      "memoryStats",
      "description",
      "address",
      "clients",
      "deviceTag",
      "joinedOn",
    ],
    enableColumnPinning: true,
    defaultPinning: { left: ["select", "linkStatus", "deviceName"], right: [] },
    enableColumnOrder: true,
    enableRowSelection: true,
  });

  const currentTableInstance = useMemo(() => {
    if (onTab) {
      return (
        (tabVal === "all_device" && allDevicesTableInstance) ||
        (tabVal === "access_point" && apTableInstance) ||
        (tabVal === "switch" && swTableInstance) ||
        (tabVal === "router" && rtrTableInstance) ||
        (tabVal === "inventory" && invTableInstance) ||
        (tabVal === "proxy" && proxyTableInstance)
      );
    } else if (!onTab && clickSiteInventory) {
      return invTableInstance;
    } else if (!onTab && clickSiteUnityProxy) {
      return proxyTableInstance;
    }
  }, [
    tabVal,
    allDevicesTableInstance,
    apTableInstance,
    swTableInstance,
    rtrTableInstance,
    invTableInstance,
    proxyTableInstance,
    onTab,
    clickSiteInventory,
    clickSiteUnityProxy,
  ]);

  const currentTableSelected = useMemo(
    () =>
      currentTableInstance &&
      currentTableInstance.table.getSelectedRowModel().rows,
    [currentTableInstance],
  );

  const drawerDataMap = useMemo(
    () => ({
      all_device: {
        info: tableDataAll,
        network: tableDataNetworkAll,
        port: tableDataPortsAll,
        routerInternet: tableDataInternetAll,
        dhcp: tableDataDhcpAll,
        capabilities: tableDataCapabilitiesAll,
        managedDevices: tableDataManagedDevicesAll,
        apRadio: tableDataApRadioAll,
        "wireless.radio": tableDataWirelessRadioAll,
        "wireless.network": tableDataWirelessNetworkAll,
      },
      access_point: {
        info: tableDataAp,
        network: tableDataNetworkAp,
        port: tableDataPortsAp,
        capabilities: tableDataCapabilitiesAp,
        apRadio: tableDataApRadioAp,
        "wireless.radio": tableDataWirelessRadioAp,
        "wireless.network": tableDataWirelessNetworkAp,
      },
      switch: {
        info: tableDataSw,
        network: tableDataNetworkSw,
        port: tableDataPortsSw,
        capabilities: tableDataCapabilitiesSw,
      },
      router: {
        info: tableDataRtr,
        network: tableDataNetworkRtr,
        port: tableDataPortsRtr,
        capabilities: tableDataCapabilitiesRtr,
        routerInternet: tableDataInternetRtr,
        dhcp: tableDataDhcpRtr,
      },
      inventory: {
        info: tableDataInv,
        network: tableDataNetworkInv,
        port: tableDataPortsInv,
        capabilities: tableDataCapabilitiesInv,
        apRadio: tableDataApRadioInv,
        "wireless.radio": tableDataWirelessRadioInv,
        "wireless.network": tableDataWirelessNetworkInv,
      },
      proxy: {
        info: tableDataProxy,
        network: tableDataNetworkProxy,
        port: tableDataPortsProxy,
        capabilities: tableDataCapabilitiesProxy,
        managedDevices: tableDataManagedDevicesProxy,
      },
    }),
    [
      tableDataAll,
      tableDataNetworkAll,
      tableDataPortsAll,
      tableDataInternetAll,
      tableDataDhcpAll,
      tableDataWirelessRadioAll,
      tableDataWirelessNetworkAll,
      tableDataManagedDevicesAll,
      tableDataAp,
      tableDataNetworkAp,
      tableDataPortsAp,
      tableDataWirelessRadioAp,
      tableDataWirelessNetworkAp,
      tableDataSw,
      tableDataNetworkSw,
      tableDataPortsSw,
      tableDataRtr,
      tableDataNetworkRtr,
      tableDataPortsRtr,
      tableDataInternetRtr,
      tableDataDhcpRtr,
      tableDataInv,
      tableDataNetworkInv,
      tableDataPortsInv,
      tableDataCapabilitiesAll,
      tableDataCapabilitiesAp,
      tableDataCapabilitiesSw,
      tableDataCapabilitiesInv,
      tableDataWirelessRadioInv,
      tableDataWirelessNetworkInv,
      tableDataProxy,
      tableDataNetworkProxy,
      tableDataPortsProxy,
      tableDataCapabilitiesProxy,
      tableDataManagedDevicesProxy,
      tableDataApRadioInv,
      tableDataApRadioAp,
      tableDataApRadioAll,
    ],
  );

  const currentDeviceIndex = useMemo(() => {
    if (!drawerOpen || !currentDrawerData.id) return -1;
    const currentTab =
      tabVal ||
      (clickSiteInventory ? "inventory" : clickSiteUnityProxy ? "proxy" : null);
    if (!currentTab || !drawerDataMap[currentTab]) return -1;
    const index = drawerDataMap[currentTab].info.findIndex(
      (d) => d.id === currentDrawerData.id,
    );

    return index;
  }, [
    drawerOpen,
    currentDrawerData.id,
    tabVal,
    clickSiteInventory,
    clickSiteUnityProxy,
    drawerDataMap,
  ]);

  const { dataMap, getData, sendRequest, sendBatch } = useDataSourceManager();

  const showDiscoverBatchAction = useMemo(() => {
    return (
      clickDiscover &&
      (discoverBatchActionsStatus.ipSetting ||
        discoverBatchActionsStatus.auth ||
        discoverBatchActionsStatus.adopt)
    );
  }, [discoverBatchActionsStatus]);

  const getIds = () => ({
    realmId: auth.realm?.id || "",
    orgId: auth.orgs?.id || "",
  });

  const fetchLicenseData = async () => {
    try {
      const realmId = auth.realm?.id || "";
      const orgId = auth.orgs?.id || "";

      const restfulParams = standalone
        ? { orgId: realmId }
        : { entId: realmId, orgId };

      const settingsApiKey = standalone
        ? "/orgs/:orgId/settings/general.read"
        : "/ents/:entId/orgs/:orgId/settings/general.read";

      const { getSendBatchData } = await sendBatch([
        {
          apiKey: settingsApiKey,
          requestUuid: settingsUuid,
          restfulPlaceholders: restfulParams,
          params: {},
        },
      ]);

      const settingsRes = getSendBatchData({ requestUuid: settingsUuid });

      if (settingsRes?.data) {
        setSettingsData(settingsRes.data);
      }
    } catch (error) {
      console.error(
        "[Devices] Error fetching license data via sendBatch:",
        error,
      );
    } finally {
      setLicenseDataLoaded(true);
    }
  };

  function fetchTableData(forceReload = false) {
    setLoading(true);
    if (
      auth.scopeLevel === "org" &&
      ((checkEnvironment === "premises" && auth.realm) ||
        (auth.realm && auth.orgs))
    ) {
      handleTableDataRequest(
        {
          all: widgetAllUuid,
          ap: widgetApUuid,
          sw: widgetSwUuid,
          rtr: widgetRtrUuid,
          inv: widgetInvUuid,
          proxy: widgetProxyUuid,
        },
        forceReload ? "all_device" : tabVal,
        "orgTable",
        sendRequest,
        { realm: auth.realm.id, org: auth.orgs.id },
      );
    }

    if (
      auth.scopeLevel === "site" &&
      ((checkEnvironment === "premises" && auth.realm && auth.sites) ||
        (auth.realm && auth.orgs && auth.sites))
    ) {
      handleTableDataRequest(
        {
          all: widgetAllUuid,
          ap: widgetApUuid,
          sw: widgetSwUuid,
          rtr: widgetRtrUuid,
          inv: widgetInvUuid,
          proxy: widgetProxyUuid,
        },
        forceReload ? "all_device" : tabVal,
        "siteTable",
        sendRequest,
        { realm: auth.realm.id, org: auth.orgs.id, site: auth.sites.id },
      );
    }
  }

  function fetchSiteProxyDevicesData() {
    const resource = handleSiteProxyDevicesRequest({
      realm: auth.realm.id,
      org: auth.orgs.id,
      site: auth.sites.id,
    });
    const requestNotifications = {
      jsonrpc: "2.0",
      id: proxyDevicesUuid,
      method: "read",
      params: {
        resource,
        accessToken: token,
        type: "proxy",
      },
    };
    ws.send(JSON.stringify(requestNotifications));
  }

  const fetchSiteInventoryTableData = () => {
    setLoading(true);
    handleSiteInventoryRequest(widgetSiteInvUuid, sendRequest, {
      realm: auth.realm.id,
      org: auth.orgs.id,
      site: auth.sites.id,
    });
  };

  function makeConnectBarData(all) {
    const datas = [];
    const lastStatus = all.linkStatus === "online" ? "Online" : "Offline";
    const NOW = Math.floor(Date.now() / 1000); // 目前時間（秒）
    const DAY_24_HOURS = 24 * 60 * 60;
    const START_24H_AGO = NOW - DAY_24_HOURS;

    if (
      all.connectivityTimeline !== null &&
      all.connectivityTimeline.length > 0
    ) {
      const sorted = [...all.connectivityTimeline].sort(
        (a, b) => a.createdAt - b.createdAt,
      );
      const firstStatus =
        sorted[0].type === "DEVICE_ONLINE" ? "Online" : "Offline";
      const oppositeStatus = firstStatus === "Online" ? "Offline" : "Online";
      //補開頭
      if (sorted[0].createdAt > START_24H_AGO) {
        datas.push({
          name: oppositeStatus,
          value: [START_24H_AGO, sorted[0].createdAt],
        });
      }

      for (let i = 0; i < sorted.length; i++) {
        const start = sorted[i].createdAt;
        const end = i < sorted.length - 1 ? sorted[i + 1].createdAt : NOW;
        const record = {
          name: sorted[i].type === "DEVICE_ONLINE" ? "Online" : "Offline",
          value: [start, end],
        };

        //對照 linkStatus 判斷要不要補最後一筆(60s)
        if (i >= sorted.length - 1) {
          if (record.name === lastStatus) {
            datas.push(record);
          } else {
            let preEnd = end - 60;
            if (preEnd < start) {
              preEnd = end - 1;
            }
            record.value[1] = preEnd;

            datas.push(record);
            datas.push({
              name: lastStatus,
              value: [preEnd, NOW],
            });
          }
        } else {
          datas.push(record);
        }
      }
    } else {
      //無上報, 根據 linkStatus 產生
      datas.push({
        name: lastStatus,
        value: [START_24H_AGO, NOW],
      });
    }
    return datas;
  }

  useEffect(() => {
    fetchTableData();
  }, [auth.scopeLevel, auth.realm, auth.orgs, auth.sites, tabVal]);

  useEffect(() => {
    const responseWidgetConfigs = [
      {
        uuid: widgetAllUuid,
        setter: (
          allList,
          deviceNetwork,
          ports,
          allInternetList,
          dhcpList,
          capabilities,
          wirelessRadio,
          wirelessNetwork,
          managedDevices,
          apRadio,
        ) => {
          setTableDataAll(allList);
          setTableDataNetworkAll(deviceNetwork);
          setTableDataPortsAll(ports);
          setTableDataInternetAll(allInternetList);
          setTableDataDhcpAll(dhcpList);
          setTableDataCapabilitiesAll(capabilities);
          setTableDataWirelessRadioAll(wirelessRadio);
          setTableDataWirelessNetworkAll(wirelessNetwork);
          setTableDataManagedDevicesAll(managedDevices);
          setTableDataApRadioAll(apRadio);
        },
        countSetter: (d, i) => {
          setTabCountAll(d.length);
          setTabCountAp(d.filter((item) => item.deviceType === "ap").length);
          setTabCountSw(
            d.filter((item) => item.deviceType === "switch").length,
          );
          setTabCountRtr(
            d.filter((item) => item.deviceType === "router").length,
          );
          setTabCountInv(i.length);
          setTabCountProxy(
            d.filter((item) => item.deviceType === "proxy").length,
          );
        },
      },
      {
        uuid: widgetApUuid,
        setter: (
          apList,
          deviceNetwork,
          ports,
          capabilities,
          wirelessRadio,
          wirelessNetwork,
          apRadio,
        ) => {
          setTableDataAp(apList);
          setTableDataNetworkAp(deviceNetwork);
          setTableDataPortsAp(ports);
          setTableDataCapabilitiesAp(capabilities);
          setTableDataWirelessRadioAp(wirelessRadio);
          setTableDataWirelessNetworkAp(wirelessNetwork);
          setTableDataApRadioAp(apRadio);
        },
        countSetter: (d) => {
          setTabCountAp(d.filter((item) => item.deviceType === "ap").length);
        },
      },
      {
        uuid: widgetSwUuid,
        setter: (swList, deviceNetwork, ports, capabilities) => {
          setTableDataSw(swList);
          setTableDataNetworkSw(deviceNetwork);
          setTableDataPortsSw(ports);
          setTableDataCapabilitiesSw(capabilities);
        },
        countSetter: (d) => {
          setTabCountSw(
            d.filter((item) => item.deviceType === "switch").length,
          );
        },
      },
      {
        uuid: widgetRtrUuid,
        setter: (
          rtrList,
          deviceNetwork,
          ports,
          capabilities,
          rtrInternetList,
          dhcpList,
        ) => {
          setTableDataRtr(rtrList);
          setTableDataNetworkRtr(deviceNetwork);
          setTableDataPortsRtr(ports);
          setTableDataCapabilitiesRtr(capabilities);
          setTableDataInternetRtr(rtrInternetList);
          setTableDataDhcpRtr(dhcpList);
        },
        countSetter: (d) => {
          setTabCountRtr(
            d.filter((item) => item.deviceType === "router").length,
          );
        },
      },
      {
        uuid: widgetInvUuid,
        setter: (
          invList,
          deviceNetwork,
          ports,
          capabilities,
          wirelessRadio,
          wirelessNetwork,
          apRadio,
        ) => {
          setTableDataInv(invList);
          setTableDataNetworkInv(deviceNetwork);
          setTableDataPortsInv(ports);
          setTableDataCapabilitiesInv(capabilities);
          setTableDataWirelessRadioInv(wirelessRadio);
          setTableDataWirelessNetworkInv(wirelessNetwork);
          setTableDataApRadioInv(apRadio);
        },
        countSetter: (d, i) => {
          setTabCountAll(d.length);
          setTabCountAp(d.filter((item) => item.deviceType === "ap").length);
          setTabCountSw(
            d.filter((item) => item.deviceType === "switch").length,
          );
          setTabCountInv(i.length);
          setTabCountProxy(
            d.filter((item) => item.deviceType === "proxy").length,
          );
        },
      },
      {
        uuid: widgetProxyUuid,
        setter: (
          proxyList,
          deviceNetwork,
          ports,
          capabilities,
          managedDevices,
        ) => {
          setTableDataProxy(proxyList);
          setTableDataNetworkProxy(deviceNetwork);
          setTableDataPortsProxy(ports);
          setTableDataCapabilitiesProxy(capabilities);
          setTableDataManagedDevicesProxy(managedDevices);
        },
        countSetter: (d) => {
          setTabCountProxy(
            d.filter((item) => item.deviceType === "proxy").length,
          );
        },
      },
    ];

    let processedOrgLicense = false;
    responseWidgetConfigs.forEach(({ uuid, setter, countSetter }) => {
      const response = getData(uuid);
      if (!response) {
        return;
      }
      if (auth.scopeLevel === "org") {
        processedOrgLicense = true;
      }

      const updatedDiscoverProxyDevices = [];
      if (response && response.data) {
        const data = response.data;

        // [01]Collect bound devices and unbound devices
        const infoList = []; // For counting total devices
        const inventoryList = []; // For counting inventory devices
        const allList = []; // For counting all devices
        const apList = []; // For counting access points
        const swList = []; // For counting switches
        const rtrList = []; // For counting switches
        const invList = []; // For counting inventory devices
        const proxyList = []; // For counting proxy devices

        // [02]Belong to access points and switches
        const allDeviceNetworkList = [];
        const apDeviceNetworkList = [];
        const swDeviceNetworkList = [];
        const rtrDeviceNetworkList = [];
        const invDeviceNetworkList = [];
        const proxyDeviceNetworkList = [];

        // [03]Belong to access points and switches
        const allCapabilityList = [];
        const apCapabilityList = [];
        const swCapabilityList = [];
        const rtrCapabilityList = [];
        const invCapabilityList = [];
        const proxyCapabilityList = [];

        // [04]Belong to access points
        const allPortList = [];
        const apPortList = [];
        const swPortList = [];
        const rtrPortList = [];
        const invPortList = [];
        const proxyPortList = [];

        // [05]Belong to access points
        const allWirelessRadioList = [];
        const apWirelessRadioList = [];
        const invWirelessRadioList = [];

        // [06]Belong to access points
        const allWirelessNetworkList = [];
        const apWirelessNetworkList = [];
        const invWirelessNetworkList = [];

        // [07]Belong to proxy
        const allManagedDevicesList = [];
        const proxyManagedDevicesList = [];

        // routerInternet
        const allInternetList = [];
        const rtrInternetList = [];

        // dhcp
        const allDhcpList = [];
        const rtrDhcpList = [];

        // ApRadio
        const allApRadioList = [];
        const apApRadioList = [];
        const invApRadioList = [];

        for (let i = 0; i < data.length; i++) {
          const item = data[i];
          infoList.push(item.info);
          if (!item.info.siteName && item.info.deviceType !== "proxy") {
            inventoryList.push(item.info);
          }

          allList.push({
            index: allList.length,
            ...item.info,
          });

          // deviceIdList.push(item.info.macAddress)
          allDeviceNetworkList.push(item.deviceNetwork || {});
          allPortList.push(
            item.info.deviceType === "ap" ? {} : item.switchPorts || {},
          );
          allInternetList.push(item.routerInternet || {});
          allDhcpList.push(item.dhcpLease || []);
          allCapabilityList.push(item.capabilities || {});
          allWirelessRadioList.push(item["wireless.radio"] || {});
          allWirelessNetworkList.push(item["wireless.network"] || {});
          allManagedDevicesList.push(item.managedDevices || {});


          allApRadioList.push(item.apRadio || {});

          if (item.info.deviceType === "ap") {
            apList.push({
              index: apList.length,
              ...item.info,
            });
            apDeviceNetworkList.push(item.deviceNetwork || {});
            apPortList.push({});
            apCapabilityList.push(item.capabilities || {});
            apWirelessRadioList.push(item["wireless.radio"] || []);
            apWirelessNetworkList.push(item["wireless.network"] || {});
            apApRadioList.push(item.apRadio || {});
          }

          if (item.info.deviceType === "switch") {
            swList.push({
              index: swList.length,
              ...item.info,
            });
            swDeviceNetworkList.push(item.deviceNetwork || {});
            swPortList.push(item.switchPorts || {});
            swCapabilityList.push(item.capabilities || {});
          }

          if (item.info.deviceType === "router") {
            rtrList.push({
              index: rtrList.length,
              ...item.info,
            });
            rtrDeviceNetworkList.push(item.deviceNetwork || {});
            rtrPortList.push(item.switchPorts || {});
            rtrCapabilityList.push(item.capabilities || {});
            rtrInternetList.push(item.routerInternet || {});
            rtrDhcpList.push(item.dhcpLease || []);
          }
          if (!item.info.siteName && item.info.deviceType !== "proxy") {
            invList.push({
              index: invList.length,
              ...item.info,
            });
            invDeviceNetworkList.push(item.deviceNetwork || {});
            invPortList.push(
              item.info.deviceType === "ap" ? {} : item.switchPorts || {},
            );
            invCapabilityList.push(item.capabilities || {});
            invWirelessRadioList.push(item["wireless.radio"] || []);
            invWirelessNetworkList.push(item["wireless.network"] || {});
            invApRadioList.push(item.apRadio || {});
          }

          if (item.info.deviceType === "proxy") {
            proxyList.push({
              index: proxyList.length,
              ...item.info,
            });

            proxyDeviceNetworkList.push(item.deviceNetwork || {});
            proxyPortList.push({});
            proxyCapabilityList.push(item.capabilities || {});
            proxyManagedDevicesList.push(item.managedDevices || {});

            updatedDiscoverProxyDevices.push({
              productCategory: item.info.deviceType,
              deviceName: item.info.deviceName,
              proxyUuid: item.info.id,
              ipAddress: item.info.localIpAddr,
              linkStatus: item.info.linkStatus,
            });
          }
        }

        if (uuid === widgetAllUuid) {
          setter(
            allList,
            allDeviceNetworkList,
            allPortList,
            allInternetList,
            allDhcpList,
            allCapabilityList,
            allWirelessRadioList,
            allWirelessNetworkList,
            allManagedDevicesList,
            allApRadioList,
          );

          const merged = allList.map((all, index) => {
            const radios = Array.isArray(allWirelessRadioList[index]) // {},{},{},[{},{}]
              ? allWirelessRadioList[index]
              : [];

            const radio2 = radios.find((r) => r.band === "2");
            const radio5 = radios.find((r) => r.band === "5");
            const radio6 = radios.find((r) => r.band === "6");

            let clients;

            const wirelessNetwork = Array.isArray(allWirelessNetworkList[index])
              ? allWirelessNetworkList[index]
              : [];

            switch (all.deviceType) {
              case "switch":
                clients = all.client;
                break;

              case "ap":
                clients = wirelessNetwork.reduce(
                  (sum, item) => sum + item.client,
                  0,
                );
                break;

              case "proxy":
                clients = "-";
                break;

              case "router":
                clients = all.connectedClient;
                break;

              default:
                clients = 0;
            }

            const uplink = allDeviceNetworkList[index];
            const connectInfo = makeConnectBarData(all);
            const isProxyModel =
              all.modelName && all.modelName.toLowerCase().includes("proxy");

            return {
              ...all,
              connect: connectInfo,
              apRadio: allApRadioList[index] || {},
              2: radio2 ? `${radio2.channel ?? "-"}` : "-",
              5: radio5 ? `${radio5.channel ?? "-"}` : "-",
              6: radio6 ? `${radio6.channel ?? "-"}` : "-",
              uplinkDeviceMac: uplink.uplinkDeviceMac ?? "",
              uplinkDeviceName: uplink.uplinkDeviceName ?? "",
              clients,
              traffic24HrUsage: isProxyModel ? null : all.traffic24HrUsage,
            };
          });
          setMergedAllTableData(merged);
        }

        if (uuid === widgetApUuid) {
          setter(
            apList,
            apDeviceNetworkList,
            apPortList,
            apCapabilityList,
            apWirelessRadioList,
            apWirelessNetworkList,
            apApRadioList,
          );
          const merged = apList.map((ap, index) => {
            const radios = apWirelessRadioList[index] || [];

            const radio2 = radios.find((r) => r.band === "2");
            const radio5 = radios.find((r) => r.band === "5");
            const radio6 = radios.find((r) => r.band === "6");

            const wirelessNetwork = Array.isArray(apWirelessNetworkList[index])
              ? apWirelessNetworkList[index]
              : [];

            const uplink = apDeviceNetworkList[index];
            const connectInfo = makeConnectBarData(ap);

            return {
              ...ap,
              connect: connectInfo,
              apRadio: apApRadioList[index] || {},
              2: radio2 ? `${radio2.channel ?? "-"}` : "-",
              5: radio5 ? `${radio5.channel ?? "-"}` : "-",
              6: radio6 ? `${radio6.channel ?? "-"}` : "-",
              uplinkDeviceMac: uplink.uplinkDeviceMac ?? "",
              uplinkDeviceName: uplink.uplinkDeviceName ?? "",
              clients: wirelessNetwork.reduce(
                (sum, item) => sum + item.client,
                0,
              ),
            };
          });
          setMergedApTableData(merged);
        }

        if (uuid === widgetSwUuid) {
          setter(swList, swDeviceNetworkList, swPortList, swCapabilityList);

          const merged = swList.map((sw, index) => {
            const uplink = swDeviceNetworkList[index];
            const connectInfo = makeConnectBarData(sw);

            return {
              ...sw,
              connect: connectInfo,
              uplinkDeviceMac: uplink.uplinkDeviceMac ?? "",
              uplinkDeviceName: uplink.uplinkDeviceName ?? "",
              clients: sw.client,
            };
          });
          setMergedSwTableData(merged);
        }

        if (uuid === widgetRtrUuid) {
          setter(
            rtrList,
            rtrDeviceNetworkList,
            rtrPortList,
            rtrCapabilityList,
            rtrInternetList,
            rtrDhcpList,
          );

          const merged = rtrList.map((rtr, index) => {
            const uplink = rtrDeviceNetworkList[index];
            const connectInfo = makeConnectBarData(rtr);

            return {
              ...rtr,
              connect: connectInfo,
              uplinkDeviceMac: uplink.uplinkDeviceMac ?? "",
              uplinkDeviceName: uplink.uplinkDeviceName ?? "",
              clients: rtr.connectedClient,
            };
          });
          setMergedRtrTableData(merged);
        }

        if (uuid === widgetInvUuid) {
          setter(
            invList,
            invDeviceNetworkList,
            invPortList,
            invCapabilityList,
            invWirelessRadioList,
            invWirelessNetworkList,
            invApRadioList,
          );

          const merged = invList.map((inv, index) => {
            const uplink = invDeviceNetworkList[index];
            const connectInfo = makeConnectBarData(inv);

            let clients;

            const wirelessNetwork = Array.isArray(invWirelessNetworkList[index])
              ? invWirelessNetworkList[index]
              : [];

            switch (inv.deviceType) {
              case "switch":
                clients = inv.client;
                break;

              case "ap":
                clients = wirelessNetwork.reduce(
                  (sum, item) => sum + item.client,
                  0,
                );
                break;

              case "proxy":
                clients = "-";
                break;

              default:
                clients = 0;
            }

            const isProxyModel =
              inv.modelName && inv.modelName.toLowerCase().includes("proxy");

            return {
              ...inv,
              apRadio: invApRadioList[index] || {},
              connect: connectInfo,
              uplinkDeviceMac: uplink.uplinkDeviceMac ?? "",
              uplinkDeviceName: uplink.uplinkDeviceName ?? "",
              clients,
              traffic24HrUsage: isProxyModel ? null : inv.traffic24HrUsage,
            };
          });

          setMergedInvTableData(merged);
        }

        if (uuid === widgetProxyUuid) {
          setter(
            proxyList,
            proxyDeviceNetworkList,
            proxyPortList,
            proxyCapabilityList,
            proxyManagedDevicesList,
          );

          const merged = proxyList.map((proxy, index) => {
            const uplink = proxyDeviceNetworkList[index] || {};
            const connectInfo = makeConnectBarData(proxy);
            const isProxyModel =
              proxy.modelName && proxy.modelName.toLowerCase().includes("proxy");

            return {
              ...proxy,
              connect: connectInfo,
              uplinkDeviceMac: uplink.uplinkDeviceMac ?? "",
              uplinkDeviceName: uplink.uplinkDeviceName ?? "",
              traffic24HrUsage: isProxyModel ? null : proxy.traffic24HrUsage,
            };
          });

          setMergedProxyTableData(merged);
        }

        if (uuid === widgetAllUuid || uuid === widgetInvUuid) {
          countSetter(infoList, inventoryList);
        } else {
          countSetter(infoList);
        }

        if (updatedDiscoverProxyDevices.length > 0) {
          setHaveProxyDevices(true);
          setDiscoverProxyDevices(updatedDiscoverProxyDevices);
        }

        setLoading(false);

        if (reloadSiteInventory) {
          fetchSiteInventoryTableData();
        }
      }
    });

    if (processedOrgLicense) {
      fetchLicenseData();
    }

    const siteInvData = getData(widgetSiteInvUuid);
    if (siteInvData) {
      const data = siteInvData.data;
      const inventoryList = [];
      const deviceNetworkList = [];
      const portList = [];
      const capabilityList = [];
      const wirelessRadioList = [];
      const wirelessNetworkList = [];
      const managedDevicesList = [];
      const apRadioList = [];

      for (let i = 0; i < data.length; i++) {
        const item = data[i];
        inventoryList.push({
          index: inventoryList.length,
          ...item.info,
        });
        deviceNetworkList.push(item.deviceNetwork || {});
        portList.push(
          item.info.deviceType === "switch" ? item.switchPorts || {} : {},
        );
        capabilityList.push(item.capabilities || {});
        wirelessRadioList.push(item["wireless.radio"] || []);
        wirelessNetworkList.push(item["wireless.network"] || {});
        managedDevicesList.push(item.managedDevices || {});
        apRadioList.push(item.apRadio || {});
      }

      setTableDataInv(inventoryList);
      setTableDataNetworkInv(deviceNetworkList);
      setTableDataPortsInv(portList);
      setTableDataCapabilitiesInv(capabilityList);
      setTableDataWirelessRadioInv(wirelessRadioList);
      setTableDataWirelessNetworkInv(wirelessNetworkList);
      setTableDataApRadioInv(apRadioList);

      const merged = inventoryList.map((inv, index) => {
        const uplink = deviceNetworkList[index];
        const connectInfo = makeConnectBarData(inv);
        const isProxyModel =
          inv.modelName && inv.modelName.toLowerCase().includes("proxy");

        return {
          ...inv,
          connect: connectInfo,
          uplinkDeviceMac: uplink.uplinkDeviceMac ?? "",
          uplinkDeviceName: uplink.uplinkDeviceName ?? "",
          traffic24HrUsage: isProxyModel ? null : inv.traffic24HrUsage,
        };
      });

      setMergedInvTableData(merged);
      setLoading(false);
      setReloadSiteInventory(false);

      if (auth.scopeLevel === "site") {
        fetchSiteProxyDevicesData();
      }
    }
  }, [
    dataMap[widgetAllUuid],
    dataMap[widgetApUuid],
    dataMap[widgetSwUuid],
    dataMap[widgetRtrUuid],
    dataMap[widgetInvUuid],
    dataMap[widgetProxyUuid],
    dataMap[widgetSiteInvUuid],
  ]);

  useEffect(() => {
    if (preTab) {
      fetchTableData();
      setPreTab(null);
    }
  }, [preTab]);

  const tabData = [
    {
      name: "All",
      value: "all_device",
      disabled: null,
      tabCount: tabCountAll,
    },
    {
      name: "Access Points",
      value: "access_point",
      disabled: null,
      tabCount: tabCountAp,
    },
    {
      name: "Switches",
      value: "switch",
      disabled: null,
      tabCount: tabCountSw,
    },
    {
      name: "Routers",
      value: "router",
      disabled: null,
      tabCount: tabCountRtr,
    },
  ];

  if (auth.scopeLevel === "org") {
    tabData.push({
      name: "Inventory",
      value: "inventory",
      disabled: null,
      tabCount: tabCountInv,
    });
    tabData.push({
      name: "Unity Proxy",
      value: "proxy",
      disabled: null,
      tabCount: tabCountProxy,
    });
  }

  const addDeviceTab = (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: "5px",
          marginTop: "2px",
          paddingBottom: "17px",
          transform: "translateY(10px)",
          cursor: "pointer",
          color: !onTab ? "primary.main" : "primary.greyThird",
        }}
        onClick={() => {
          setOpenAction(false);
          handleDrawerClose();
          setOpenAddDeviceAction(!openAddDeviceAction);
        }}
      >
        <AddBtn
          sx={{
            filter: !onTab ? "grayscale(100%)" : "none",
            opacity: !onTab ? "1" : "0.5",
            width: "20px",
          }}
        />
        ADD DEVICE
      </Box>
      <Box
        sx={{
          height: "5px",
          borderRadius: "1em",
          backgroundColor: !onTab ? "primary.main" : "primary.white",
        }}
      />
      {openAddDeviceAction && (
        <Box
          sx={{
            position: "absolute",
            top: "100%",
            left: 10,
            mt: -1,
            p: 1,
            width: "251px",
            bgcolor: "primary.lightBlue",
            padding: "10px 20px",
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
            borderRadius: 4,
            zIndex: 1300,
            "::before": {
              content: '""',
              position: "absolute",
              top: "-8px",
              left: "14px",
              width: 0,
              height: 0,
              borderLeft: "8px solid transparent",
              borderRight: "8px solid transparent",
              borderBottom: `8px solid ${theme.palette.primary.lightBlue}`,
            },
          }}
        >
          <Box
            className={mainStyle["action_item"]}
            onClick={() => {
              setOpenAddDeviceAction(false);
              setShowProvisionApp(true);
            }}
          >
            <img src={btn_provision_app} alt="" />
            <span>Provision App</span>
          </Box>
          {haveProxyDevices && (
            <>
              <Box
                className={mainStyle["action_item"]}
                onClick={() => {
                  setPreTabForAutoDiscover(tabVal);
                  setPreTab(tabVal);
                  setTabVal(null);
                  setClickSiteInventory(false);
                  setClickSiteUnityProxy(false);
                  setClickDiscover(true);
                  setOpenAddDeviceAction(false);
                }}
              >
                <img src={btn_rediscover} alt="" />
                <span>Discover</span>
              </Box>
            </>
          )}
          {auth.scopeLevel === "site" && (
            <>
              <Box
                className={mainStyle["action_item"]}
                onClick={() => {
                  setTabVal(null);
                  setClickDiscover(false);
                  setClickSiteInventory(true);
                  setClickSiteUnityProxy(false);
                  fetchSiteInventoryTableData();
                  setOpenAddDeviceAction(false);
                }}
              >
                <img src={btn_inventory} alt="" />
                <span>From Inventory</span>
              </Box>
            </>
          )}
          {portalConfig?.enableAutoProvision && (
            <>
              <Box
                className={mainStyle["action_item"]}
                onClick={() => {
                  setOpenAddDeviceAction(false);
                  setShowPreRegisterModal(true);
                }}
              >
                <img src={btn_pre_register} alt="" />
                <span>Pre Register</span>
              </Box>
            </>
          )}
        </Box>
      )}
    </>
  );

  useEffect(() => {
    if (navigateDeviceName) {
      const matchedDevice = tableDataAll.find(
        (device) => device.deviceName === navigateDeviceName,
      );

      if (matchedDevice && licenseDataLoaded) {
        setCurrentDrawerData(structuredClone(matchedDevice));
        setDrawerOpen(true);

        const { navigateDeviceName, ...resetStates } = location?.state;

        navigate(location.pathname, {
          replace: true,
          state: resetStates,
        });
      }
    }
  }, [navigateDeviceName, tableDataAll, licenseDataLoaded]);

  useEffect(() => {
    if (
      checkMockup === "false" &&
      (auth.realm || auth.orgs) &&
      auth.scopeLevel === "site" &&
      loading === false &&
      mergedProxyTableData.length === 0
    ) {
      fetchSiteProxyDevicesData();
    }
  }, [auth, loading]);

  useEffect(() => {
    if (auth.scopeLevel === "org") return;
    if (!ws) return;

    const handleFetchProxyDevices = (event) => {
      const data = JSON.parse(event.data);
      if (data.id !== proxyDevicesUuid) return;

      if (data?.result?.data?.length > 0) {
        const updatedDiscoverProxyDevices = [];
        const { data: originData } = data.result;

        originData.forEach((device) => {
          updatedDiscoverProxyDevices.push({
            productCategory: device.info.deviceType,
            deviceName: device.info.deviceName,
            linkStatus: device.info.linkStatus,
            proxyUuid: device.info.id,
            ipAddress: device.info.localIpAddr,
          });
        });

        setDiscoverProxyDevices(updatedDiscoverProxyDevices);
        setHaveProxyDevices(updatedDiscoverProxyDevices.length > 0);

        const proxyList = [];
        const deviceNetworkList = [];
        const portList = [];
        const capabilityList = [];

        for (let i = 0; i < originData.length; i++) {
          const item = originData[i];
          proxyList.push({
            index: proxyList.length,
            ...item.info,
          });
          deviceNetworkList.push(item.deviceNetwork || {});
          portList.push({});
          capabilityList.push(item.capabilities || {});
        }

        setTableDataProxy(proxyList);
        setTableDataNetworkProxy(deviceNetworkList);
        setTableDataPortsProxy(portList);
        setTableDataCapabilitiesProxy(capabilityList);

        const merged = proxyList.map((proxy, index) => {
          const uplink = deviceNetworkList[index] || {};
          const connectInfo = makeConnectBarData(proxy);
          const isProxyModel =
            proxy.modelName && proxy.modelName.toLowerCase().includes("proxy");

          return {
            ...proxy,
            connect: connectInfo,
            uplinkDeviceMac: uplink.uplinkDeviceMac ?? "",
            uplinkDeviceName: uplink.uplinkDeviceName ?? "",
            traffic24HrUsage: isProxyModel ? null : proxy.traffic24HrUsage,
          };
        });

        setMergedProxyTableData(merged);
      } else {
        setDiscoverProxyDevices([]);
        setHaveProxyDevices(false);
        setTableDataProxy([]);
        setTableDataNetworkProxy([]);
        setTableDataPortsProxy([]);
        setTableDataCapabilitiesProxy([]);
        setMergedProxyTableData([]);
      }

      fetchLicenseData();
    };

    ws.addEventListener("message", handleFetchProxyDevices);

    return () => {
      ws.removeEventListener("message", handleFetchProxyDevices);
    };
  }, [ws, auth, clickDiscover]);

  return (
    <>
      <div
        className={`main-card-content ${isEmptyTable ? "main-card-content__empty_table" : ""}`}
      >
        <div>
          <Grid
            container
            sx={{ justifyContent: "space-between", marginBottom: "10px" }}
          >
            <Box>
              <Grid container sx={{ alignItems: "center" }}>
                {currentTableInstance?.table?.getSelectedRowModel()?.rows
                  ?.length > 0 && (
                    <>
                      <ActionButton
                        openAction={openAction}
                        setOpenAction={setOpenAction}
                        handleDrawerClose={handleDrawerClose}
                        setOpenAddDeviceAction={setOpenAddDeviceAction}
                        tabVal={tabVal}
                        currentTableInstance={currentTableInstance}
                        auth={auth}
                        currentTableSelected={currentTableSelected}
                        clickSiteInventory={clickSiteInventory}
                        clickSiteUnityProxy={clickSiteUnityProxy}
                        settingsData={settingsData}
                        fetchTableData={(toReloadSiteInventory = false) => {
                          setPreTab(tabVal);
                          fetchTableData(true);

                          // Reload site inventory after remove from site inventory
                          if (toReloadSiteInventory) {
                            setReloadSiteInventory(true);
                          }
                        }}
                      />
                      <Divider
                        sx={{ width: "1px", height: "40px", margin: "20px 20px" }}
                        orientation="vertical"
                        flexItem
                      />
                    </>
                  )}
                {clickDiscover && showDiscoverBatchAction && (
                  <>
                    <ActionButton
                      openAction={openAction}
                      setOpenAction={setOpenAction}
                      handleDrawerClose={handleDrawerClose}
                      setOpenAddDeviceAction={setOpenAddDeviceAction}
                      tabVal={tabVal}
                      clickDiscover={clickDiscover}
                      clickSiteInventory={clickSiteInventory}
                      itemCtrl={{
                        discover: discoverBatchActionsStatus,
                      }}
                      itemCallback={{
                        discover: (action) => {
                          setDiscoverBatchActionTarget(action);
                        },
                      }}
                    />
                    <Divider
                      sx={{
                        width: "1px",
                        height: "40px",
                        margin: "20px 20px",
                      }}
                      orientation="vertical"
                      flexItem
                    />
                  </>
                )}
                <TabsField
                  tabVal={tabVal}
                  handleTabsChange={handleTab}
                  tabData={tabData}
                />
                {auth && auth.permissions !== "Viewer" && (
                  <ClickAwayListener
                    onClickAway={() => setOpenAddDeviceAction(false)}
                  >
                    <Box sx={{ marginLeft: "5px", position: "relative" }}>
                      {addDeviceTab}
                    </Box>
                  </ClickAwayListener>
                )}
              </Grid>
            </Box>
            <Box>
              <Grid container sx={{ alignItems: "center" }}>
                <Divider
                  sx={{ width: "1px", height: "40px", margin: "20px 20px" }}
                  orientation="vertical"
                  flexItem
                />
                {(onTab ||
                  (!onTab && clickSiteInventory) ||
                  (!onTab && clickSiteUnityProxy)) && (
                    <>
                      <Box
                        sx={{
                          display: "flex",
                          padding: "3px 10px 3px",
                          marginRight: "5px",
                        }}
                      >
                        <SearchInput
                          value={currentTableInstance.globalFilter}
                          onChange={currentTableInstance.setGlobalFilter}
                        />
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "baseline",
                        }}
                      >
                        <ColumnVisibilityToggle
                          maxWidth={600}
                          {...currentTableInstance}
                          groupedData={currentVisibilityGroupData}
                          hiddenCheck={["linkStatus", "deviceName"]}
                        />
                        <Box
                          margin="5px 10px"
                          sx={{ cursor: "pointer" }}
                          onClick={fetchTableData}
                        >
                          <img src={icon_refresh} alt="" />
                        </Box>
                        <SolidButton
                          startIcon={<ExportIcon />}
                          width="100px"
                          height="32px"
                          onClick={() =>
                            exportTableDataToCSV(currentTableInstance)
                          }
                        >
                          Export
                        </SolidButton>
                      </Box>
                    </>
                  )}
                {!onTab && clickDiscover && (
                  <>
                    <SearchInput value={searchVal} onChange={setSearchVal} />
                  </>
                )}
              </Grid>
            </Box>
          </Grid>
        </div>
        {onTab &&
          (tabVal === "all_device" ||
            tabVal === "access_point" ||
            tabVal === "switch" ||
            tabVal === "router" ||
            (auth.scopeLevel === "org" && tabVal === "inventory") ||
            (auth.scopeLevel === "org" && tabVal === "proxy")) && (
            <>
              <TableField
                {...currentTableInstance}
                pendingChildren={
                  loading === true && (
                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                      <CircularProgress />
                    </Box>
                  )
                }
                drawerOpen={drawerOpen}
                selectedRowId={currentDrawerData.id}
                handleRowOnClick={(row) => {
                  setCurrentDrawerData(row);
                  setDrawerOpen(true);
                }}
              />
            </>
          )}
        {clickSiteInventory && (
          <>
            <TableField
              {...invTableInstance}
              pendingChildren={
                loading === true && (
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <CircularProgress />
                  </Box>
                )
              }
              selectedRowId={currentDrawerData.id}
              handleRowOnClick={(row) => {
                setCurrentDrawerData(row);
                setDrawerOpen(true);
              }}
            />
          </>
        )}
        {clickSiteUnityProxy && (
          <>
            <TableField
              {...proxyTableInstance}
              pendingChildren={
                loading === true && (
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <CircularProgress />
                  </Box>
                )
              }
              selectedRowId={currentDrawerData.id}
              handleRowOnClick={(row) => {
                setCurrentDrawerData(row);
                setDrawerOpen(true);
              }}
            />
          </>
        )}
        {!onTab && clickDiscover && (
          <>
            <Discover
              sendRequest={sendRequest}
              dataMap={dataMap}
              getData={getData}
              level={auth.scopeLevel}
              setIsEmptyTable={setIsEmptyTable}
              searchVal={searchVal}
              goToThePreviousTab={() => {
                if (!preTabForAutoDiscover) {
                  setTabVal("all_device");
                } else {
                  setTabVal(cloneDeep(preTabForAutoDiscover));
                }

                setPreTabForAutoDiscover(null);
                setClickDiscover(false);
              }}
              changeDiscoverBatchActionsStatus={(status) => {
                if (status === null) {
                  setDiscoverBatchActionsStatus({
                    ipSetting: false,
                    auth: false,
                    adopt: false,
                  });
                  return;
                }

                const { hasFailedAuth, hasMissingGatewayOrDNS } = status;

                setDiscoverBatchActionsStatus({
                  ipSetting: !hasFailedAuth,
                  auth: hasFailedAuth,
                  adopt: !hasFailedAuth && !hasMissingGatewayOrDNS,
                });
              }}
              discoverBatchActionTarget={discoverBatchActionTarget}
              setDiscoverBatchActionTarget={setDiscoverBatchActionTarget}
              fetchTableData={() => {
                setPreTab(tabVal);
                fetchTableData(true);
              }}
              existedProxyDevices={discoverProxyDevices}
            />
          </>
        )}
      </div>
      {drawerOpen && currentDeviceIndex !== -1 && (
        <DrawerField
          contentKey={
            (tabVal
              ? drawerDataMap[tabVal].info[currentDeviceIndex]
              : clickSiteInventory // Either Inventory or proxy
                ? drawerDataMap["inventory"].info[currentDeviceIndex]
                : drawerDataMap["proxy"].info[currentDeviceIndex]
            ).id
          }
          open={drawerOpen}
          width="auto"
          handleClose={handleDrawerClose}
          commonContent="devices"
          stepper={tabVal === "proxy" || clickSiteUnityProxy}
          contentProps={{
            handleDrawerClose: handleDrawerClose,
            tabCountAp: tabCountAp,
            tabCountInv: tabCountInv,
            tabCountSw: tabCountSw,
            tabCountProxy: tabCountProxy,
            fetchFlag: false,
            auth: auth,
            infoData: tabVal
              ? drawerDataMap[tabVal].info[currentDeviceIndex]
              : clickSiteInventory // Either Inventory or proxy
                ? drawerDataMap["inventory"].info[currentDeviceIndex]
                : drawerDataMap["proxy"].info[currentDeviceIndex],
            networkData: tabVal
              ? drawerDataMap[tabVal].network[currentDeviceIndex]
              : clickSiteInventory
                ? drawerDataMap["inventory"].network[currentDeviceIndex]
                : drawerDataMap["proxy"].network[currentDeviceIndex],
            capabilityData: tabVal
              ? drawerDataMap[tabVal].capabilities[currentDeviceIndex]
              : clickSiteInventory
                ? drawerDataMap["inventory"].capabilities[currentDeviceIndex]
                : drawerDataMap["proxy"].capabilities[currentDeviceIndex],
            managedDevicesData:
              ["add_devices", "proxy"].indexOf(tabVal) > -1 ||
                clickSiteUnityProxy
                ? drawerDataMap[tabVal]?.managedDevices?.[currentDeviceIndex]
                : null,
            portData: tabVal
              ? drawerDataMap[tabVal].port[currentDeviceIndex]
              : drawerDataMap["inventory"].port[currentDeviceIndex],
            ...(tabVal !== "switch" && {
              wirelessRadioData: tabVal
                ? drawerDataMap[tabVal]?.["wireless.radio"]?.[
                currentDeviceIndex
                ]
                : drawerDataMap["inventory"]?.["wireless.radio"]?.[
                currentDeviceIndex
                ],
              wirelessNetworkData: tabVal
                ? drawerDataMap[tabVal]?.["wireless.network"]?.[
                currentDeviceIndex
                ]
                : drawerDataMap["inventory"]?.["wireless.network"]?.[
                currentDeviceIndex
                ],
              apRadioData: tabVal
                ? drawerDataMap[tabVal]?.apRadio?.[currentDeviceIndex]
                : clickSiteInventory
                  ? drawerDataMap["inventory"]?.apRadio?.[currentDeviceIndex]
                  : null,
              ...((tabVal === "all_device" || tabVal === "router") && {
                routerInternetData:
                  drawerDataMap[tabVal].routerInternet[currentDeviceIndex],
                dhcptData: drawerDataMap[tabVal].dhcp[currentDeviceIndex],
              }),
            }),
            fetchTableData: () => {
              setPreTab(tabVal);
              fetchTableData(true);
            },
          }}
        />
      )}

      <ProvisionAppModal
        openModal={showProvisionApp}
        onCloseModal={() => setShowProvisionApp(false)}
      />

      <PreRegisterModal
        auth={auth}
        openModal={showPreRegisterModal}
        onCloseModal={() => setShowPreRegisterModal(false)}
      />
    </>
  );
};

export default Devices;
